import { observer } from 'mobx-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { SearchInput } from '@avantstay/backoffice-core'
import { IconClose, IconSearch } from '@avantstay/backoffice-vectors'

import { GlossaryStore } from '@new/__global/stores/GlossaryStore'

import { GlossarySection } from './components/GlossarySection'
import * as S from './GlossarySidebar.styles'
import {
  GlossarySection as GlossarySectionType,
  GlossarySidebarProps,
} from './GlossarySidebar.types'
import * as IS from './IndependentGlossarySidebar.styles'

function _IndependentGlossarySidebar({ searchable = true }: Partial<GlossarySidebarProps>) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [localSearchTerm, setLocalSearchTerm] = useState('')
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const location = useLocation()
  const previousPathname = useRef(location.pathname)

  const isOpen = GlossaryStore.isOpen.get()
  const storeSearchTerm = GlossaryStore.searchTerm.get()
  const highlightTarget = GlossaryStore.highlightTarget.get()
  const domain = GlossaryStore.domain
  const sections = GlossaryStore.sections

  const searchTerm = storeSearchTerm || localSearchTerm

  useEffect(() => {
    const currentPathname = location.pathname
    const prevPathname = previousPathname.current

    if (currentPathname !== prevPathname && isOpen) {
      setIsNavigating(true)
      GlossaryStore.closeGlossary()

      setTimeout(() => {
        setIsNavigating(false)
      }, 100)
    }

    previousPathname.current = currentPathname
  }, [location.pathname, isOpen])

  const handleSearchChange = useCallback(
    (value?: string) => {
      setLocalSearchTerm(value || '')
      if (storeSearchTerm) {
        GlossaryStore.clearSearch()
      }
      if (highlightTarget) {
        GlossaryStore.clearHighlight()
      }
    },
    [storeSearchTerm, highlightTarget],
  )

  const toggleSearchExpanded = useCallback(() => {
    setIsSearchExpanded(prev => {
      const newExpanded = !prev
      if (!newExpanded) {
        setLocalSearchTerm('')
        if (storeSearchTerm) {
          GlossaryStore.clearSearch()
        }
      }
      return newExpanded
    })
  }, [storeSearchTerm])

  const handleClose = useCallback(() => {
    GlossaryStore.closeGlossary()
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setLocalSearchTerm('')
      setIsSearchExpanded(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (storeSearchTerm && storeSearchTerm !== localSearchTerm) {
      setLocalSearchTerm(storeSearchTerm)
      setIsSearchExpanded(true)
    }
  }, [storeSearchTerm, localSearchTerm])

  const filteredSections = useMemo(() => {
    if (!searchTerm) return sections

    const term = searchTerm.toLowerCase()

    return sections.reduce<GlossarySectionType[]>((acc, section) => {
      const sectionMatches =
        section.title.toLowerCase().includes(term) ||
        section.description?.toLowerCase().includes(term)

      const filteredSubsections = section.subsections?.filter(
        sub =>
          sub.title.toLowerCase().includes(term) || sub.description?.toLowerCase().includes(term),
      )

      if (sectionMatches || (filteredSubsections && filteredSubsections.length > 0)) {
        acc.push({
          ...section,
          subsections: sectionMatches ? section.subsections : filteredSubsections,
        })
      }

      return acc
    }, [])
  }, [searchTerm, sections])

  useEffect(() => {
    if (highlightTarget && isOpen && contentRef.current) {
      const scrollToElement = () => {
        const targetElement = contentRef.current?.querySelector(
          `[data-section-id="${highlightTarget}"]`,
        )
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })

          targetElement.classList.add('glossary-highlight')
          setTimeout(() => {
            targetElement.classList.remove('glossary-highlight')
            GlossaryStore.clearHighlight()
          }, 3000)
        }
      }

      setTimeout(scrollToElement, 300)
    }
  }, [highlightTarget, isOpen])

  return (
    <IS.IndependentContainer isOpen={isOpen} isNavigating={isNavigating}>
      <IS.CloseButton onClick={handleClose}>
        <IconClose width={24} height={24} />
      </IS.CloseButton>

      <S.Container>
        <IS.Header isOpen={isOpen}>
          <IS.Title>Glossary</IS.Title>
          {searchable && (
            <IS.SearchContainer isExpanded={isSearchExpanded}>
              <IS.SearchIconButton isExpanded={isSearchExpanded} onClick={toggleSearchExpanded}>
                <IconSearch />
              </IS.SearchIconButton>
              <IS.SearchInputContainer isVisible={isSearchExpanded}>
                <SearchInput
                  placeholder={`Search ${domain} glossary...`}
                  onChange={handleSearchChange}
                  searchTerm={searchTerm}
                  width={220}
                  hasSearchIcon={false}
                />
              </IS.SearchInputContainer>
            </IS.SearchContainer>
          )}
        </IS.Header>

        <S.Content ref={contentRef}>
          {filteredSections.length > 0 ? (
            filteredSections.map(section => (
              <GlossarySection key={section.id} section={section} searchTerm={searchTerm} />
            ))
          ) : searchTerm ? (
            <S.EmptyState>No results found for "{searchTerm}"</S.EmptyState>
          ) : (
            <S.EmptyState>No glossary content available</S.EmptyState>
          )}
        </S.Content>
      </S.Container>
    </IS.IndependentContainer>
  )
}

export const IndependentGlossarySidebar = observer(_IndependentGlossarySidebar)
